import Auth from "../auth"
import { Empty } from "google-protobuf/google/protobuf/empty_pb"
import {
    createContext,
    Fragment,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react"
import { firstValueFrom } from "rxjs"
import { Customer, Manage, grpc, setGrpcMetadataProvider } from "../grpc"
import { IsUserEmailExistRequest } from "../protocol/manage_pb"

interface IImpersonateContext {
    asEmail: string
    targetEmail: string
    exitImpersonation: () => void
}

export const ImpersonateContext = createContext<IImpersonateContext>({
    asEmail: "",
    targetEmail: "",
    exitImpersonation: () => {},
})

ImpersonateContext.displayName = "ImpersonateContext"

export function ImpersonateProvider({ children }: { children: ReactNode }) {
    const [asEmail, setAsEmail] = useState("")
    const [targetEmail, setTargetEmail] = useState("")
    const [ready, setReady] = useState(false)
    const [sessionKey, setSessionKey] = useState(0)

    const removeAsEmailFromUrl = useCallback((reload: boolean) => {
        const currentUrl = new URL(window.location.href)
        currentUrl.searchParams.delete("asEmail")
        const newUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`

        if (reload) {
            window.location.replace(newUrl)
            return
        }

        window.history.replaceState({}, "", newUrl)
    }, [])

    useEffect(() => {
        const urlSearchParams = new URLSearchParams(window.location.search)
        const rawImpersonatedEmail = urlSearchParams.get("asEmail") || ""
        const impersonatedEmail = rawImpersonatedEmail.replace(/ /g, "+").trim()

        if (!impersonatedEmail) {
            setAsEmail("")
            setTargetEmail("")
            setReady(true)
            return
        }

        setTargetEmail(impersonatedEmail)

        const validate = async () => {
            try {
                const session = await Auth.currentSession()
                const idtoken = session.getIdToken().getJwtToken()

                const getAccountType = grpc(
                    Customer.getAccountType.bind(Customer),
                )
                const accountTypeRes = await firstValueFrom(
                    getAccountType(new Empty(), { idtoken }),
                )

                const accountType = accountTypeRes.getAccountType() || ""
                if (accountType !== "admin") {
                    setAsEmail("")
                    return
                }

                const validateEmail = grpc(Manage.isUserEmailExist.bind(Manage))
                const req = new IsUserEmailExistRequest()
                req.setEmail(impersonatedEmail)

                const result = await firstValueFrom(
                    validateEmail(req, { idtoken }),
                )

                if (result.getExists()) {
                    setAsEmail(impersonatedEmail)
                    console.log("Impersonating:", impersonatedEmail)
                } else {
                    setAsEmail("")
                }
            } catch {
                setAsEmail("")
            } finally {
                setReady(true)
            }
        }

        void validate()
    }, [])

    useEffect(() => {
        if (asEmail) {
            setGrpcMetadataProvider(() => ({ asEmail }))
        } else {
            setGrpcMetadataProvider(() => ({}))
        }

        return () => {
            setGrpcMetadataProvider(() => ({}))
        }
    }, [asEmail])

    const exitImpersonation = useCallback(() => {
        removeAsEmailFromUrl(false)
        setAsEmail("")
        setTargetEmail("")
        setGrpcMetadataProvider(() => ({}))
        setSessionKey((k) => k + 1)
    }, [removeAsEmailFromUrl])

    const value = useMemo(
        () => ({
            asEmail,
            targetEmail,
            exitImpersonation,
        }),
        [asEmail, targetEmail, exitImpersonation],
    )

    return (
        <ImpersonateContext.Provider value={value}>
            {ready ? <Fragment key={sessionKey}>{children}</Fragment> : null}
        </ImpersonateContext.Provider>
    )
}
